import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * <chz-pair-button>: agrega los dos juegos de la promo o solo el que falta.
 *
 * Usa el mismo productor de eventos que la ficha de Horizon (assets/product-form.js):
 * dispara CartLinesUpdateEvent con una promesa y pide las secciones de los cart-items-component
 * en el mismo POST, así el carrito lateral se abre, el contador se actualiza y las líneas se
 * redibujan sin un segundo sistema de carrito.
 *
 * Atributos:
 *   data-mode="set"      Agrega un ejemplar de cada juego (home, colección).
 *   data-mode="missing"  Consulta el carrito y agrega solo los juegos que faltan (ficha, carrito).
 *   data-variant-a / data-variant-b / data-product-a / data-product-b
 *   data-label-adding / data-label-added / data-label-error
 *
 * Un solo envío a la vez: mientras hay una solicitud en curso, los clics se ignoran.
 */
class ChzPairButton extends HTMLElement {
  /** @type {boolean} */
  #busy = false;
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    if (this.#timer) clearTimeout(this.#timer);
  }

  get #status() {
    return this.querySelector('[data-chz-pair-status]');
  }

  #say(text) {
    const status = this.#status;
    if (status) status.textContent = text || '';
  }

  #setBusy(isBusy) {
    this.#busy = isBusy;
    if (!this.#button) return;
    this.#button.setAttribute('aria-busy', String(isBusy));
    this.#button.disabled = isBusy;
    const label = this.#button.querySelector('.chz-btn__label');
    if (label) {
      // innerHTML: algunas etiquetas traen un tramo que se oculta en el celular ("Agregar<span> al carrito</span>").
      if (isBusy) {
        label.dataset.idle = label.innerHTML;
        label.textContent = this.dataset.labelAdding || label.textContent;
      } else if (label.dataset.idle) {
        label.innerHTML = label.dataset.idle;
      }
    }
  }

  /** @returns {Promise<any>} */
  async #getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /** @param {any} cart */
  #missingItems(cart) {
    const present = new Set((cart?.items ?? []).map((item) => String(item.product_id)));
    const items = [];
    if (this.dataset.variantA && !present.has(this.dataset.productA)) items.push({ variantId: this.dataset.variantA, quantity: 1 });
    if (this.dataset.variantB && !present.has(this.dataset.productB)) items.push({ variantId: this.dataset.variantB, quantity: 1 });
    return items;
  }

  #onClick = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    this.#setBusy(true);
    this.#say('');

    try {
      let items = [];
      if (this.dataset.mode === 'missing') {
        items = this.#missingItems(await this.#getCart());
        if (items.length === 0) {
          this.#say(this.dataset.labelAdded || '');
          return;
        }
      } else {
        items = [this.dataset.variantA, this.dataset.variantB]
          .filter(Boolean)
          .map((variantId) => ({ variantId, quantity: 1 }));
      }
      await this.#add(items);
    } catch (error) {
      console.error('[chz-pair]', error);
      this.#say(this.dataset.labelError || '');
    } finally {
      this.#setBusy(false);
    }
  };

  /** @param {{variantId: string, quantity: number}[]} items */
  async #add(items) {
    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.variantId), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ id: Number(item.variantId), quantity: item.quantity })),
          sections: sectionIds.join(','),
        }),
      }).then((r) => r.json());

      const cart = await this.#getCart();

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, itemCount: totalQuantity, source: 'chz-pair' },
        });
        this.#say(response.description || response.message || this.dataset.labelError || '');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: totalQuantity,
          sections: response.sections,
          source: 'chz-pair',
        },
      });
      this.#say(this.dataset.labelAdded || '');
      this.#timer = window.setTimeout(() => this.#say(''), 4000);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      throw error;
    }
  }
}

if (!customElements.get('chz-pair-button')) {
  customElements.define('chz-pair-button', ChzPairButton);
}
